import { geosearch } from "./api";
import { suggestBeaches, beachKey } from "../../shared/beaches";
import type { VisitedLocation } from "./types";

// Unified place search shared by every "pick a spot" surface (the Spot
// screen, the Plan search, the trip BeachPicker). One ranking, one shape:
//   1. curated snorkel spots (known water) — instant, ranked first
//   2. spots you've logged fish at — instant
//   3. general geocoded map locations — the network fallback
// Every result carries coordinates, so whatever you pick can be logged,
// planned, or attached to a trip and still plot on a map.

export type PlaceSource = "curated" | "logged" | "geo";

export interface PlaceItem {
  name: string;
  lat: number;
  lng: number;
  /** Curated region ("Big Island · Kohala") or geocoder detail ("Maui, HI"). */
  detail?: string;
  source: PlaceSource;
}

// Instant, no-network matches: curated beaches first, then your logged spots.
export function localPlaceMatches(
  query: string,
  visited: VisitedLocation[] = [],
  limit = 6,
): PlaceItem[] {
  const curated: PlaceItem[] = suggestBeaches(query, limit)
    .filter((b) => b.lat != null && b.lng != null)
    .map((b) => ({
      name: b.name,
      lat: b.lat as number,
      lng: b.lng as number,
      detail: b.region,
      source: "curated" as const,
    }));
  const seen = new Set(curated.map((c) => beachKey(c.name)));
  const q = query.trim().toLowerCase();
  const logged: PlaceItem[] = [];
  for (const v of visited) {
    const name = (v.name ?? "").trim();
    if (!name || v.lat == null || v.lng == null) continue;
    const k = beachKey(name);
    if (seen.has(k)) continue;
    if (q && !name.toLowerCase().includes(q)) continue;
    seen.add(k);
    logged.push({ name, lat: v.lat, lng: v.lng, source: "logged" });
    if (logged.length >= limit) break;
  }
  return [...curated, ...logged];
}

// Async geocoded fallback, tagged and de-duped against the local matches
// (pass their beachKeys in `exclude`).
export async function geoPlaceMatches(
  query: string,
  exclude: Set<string>,
  signal?: AbortSignal,
  limit = 6,
): Promise<PlaceItem[]> {
  const results = await geosearch(query, signal);
  const seen = new Set(exclude);
  const out: PlaceItem[] = [];
  for (const p of results) {
    const k = beachKey(p.name);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({
      name: p.name,
      lat: p.lat,
      lng: p.lng,
      detail: p.detail || undefined,
      source: "geo",
    });
    if (out.length >= limit) break;
  }
  return out;
}

export function placeKeys(items: PlaceItem[]): Set<string> {
  return new Set(items.map((i) => beachKey(i.name)));
}
